import clsx from "clsx"
import { forwardRef } from "react"

import { faIcon } from "@utils/faIcon"

import Skeleton from "@particles/Skeleton/Skeleton"

import { Icon, NavigationLink, Toggle } from "@atoms"

import styles from "./MenuItem.module.scss"
import { MenuItemProps, ToggleMenuItemProps } from "./MenuItem.types"

function MenuItem(props: MenuItemProps, ref: React.Ref<HTMLAnchorElement>) {
    const {
        variant = "default",
        className,
        icon,
        iconActive,
        showIcon = true,

        path,

        label,
        supportText,
        showSupportText,
        subtext,
        showSubtext,

        onClick,
        onActive,

        isDisabled = false,
        isActive = undefined,
        isLoading,
        _isFocused,
        _isHovered,
        isPressed,
        ...rest
    } = props

    const showSubMenu: boolean | undefined = props.variant === "toggle" ? undefined : props.showSubMenu
    const toggle: ToggleMenuItemProps["toggle"] | undefined = props.variant === "toggle" ? props.toggle : undefined

    const handleClick = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
        isDisabled ? event.preventDefault() : onClick?.(event)
    }

    return (
        <NavigationLink
            title={label}
            path={path}
            className={clsx(styles.base, className, styles[`${variant}Variant`], {
                [styles.disabled]: isDisabled,
                [styles.loading]: isLoading,
                [styles.withSupportText]: showSupportText && supportText,
                [styles.focused]: _isFocused,
                [styles.hovered]: _isHovered,
                [styles.pressed]: isPressed,
            })}
            activeClassName={styles.active}
            onClick={handleClick}
            isActive={isActive}
            onActive={onActive}
            isDisabled={isDisabled}
            ref={ref}
            {...rest}
        >
            {(isActive) => {
                if (isLoading) {
                    return (
                        <div className={styles.iconAndText}>
                            {showIcon && icon && (
                                <div>
                                    <Skeleton circle={true} width={16} height={16} />
                                </div>
                            )}
                            <span className={styles.text}>
                                <span className={styles.label}>
                                    <div>
                                        <Skeleton width="100%" height={10} borderRadius={0} />
                                    </div>
                                </span>
                                {showSupportText && (
                                    <div>
                                        <Skeleton width="100%" height={10} borderRadius={0} />
                                    </div>
                                )}
                            </span>
                        </div>
                    )
                } else {
                    return (
                        <>
                            <div className={styles.iconAndText}>
                                {showIcon && icon && (
                                    <Icon
                                        icon={icon}
                                        iconOnActive={iconActive}
                                        isActive={isActive}
                                        containerClassName={styles.icon}
                                        container="square"
                                        size={14}
                                    />
                                )}
                                <span className={styles.text}>
                                    <span className={styles.label}>{label}</span>
                                    {showSupportText && <span className={styles.supportText}>{supportText}</span>}
                                </span>
                            </div>
                            {showSubtext && <span className={styles.subtext}>{subtext}</span>}
                            {toggle && (
                                <Toggle
                                    checked={toggle.isChecked}
                                    onCheckedChange={toggle.onCheckedChange}
                                    isDisabled={isDisabled}
                                />
                            )}
                            {showSubMenu && (
                                <div className={styles.chevronWrap}>
                                    <Icon
                                        size={12}
                                        icon={faIcon({ name: "angle-right", style: "regular" })}
                                        className={clsx(styles.chevron)}
                                        container="fixedHeight"
                                    />
                                </div>
                            )}
                        </>
                    )
                }
            }}
        </NavigationLink>
    )
}

export default forwardRef(MenuItem)
